import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import ErrorToast, { ToastMessage } from '../components/ErrorToast';

interface ToastContextType {
  showToast: (toast: Omit<ToastMessage, 'id'>) => void;
  showError: (title: string, message: string, action?: ToastMessage['action']) => void;
  showWarning: (title: string, message: string, action?: ToastMessage['action']) => void;
  showSuccess: (title: string, message: string, action?: ToastMessage['action']) => void;
  showInfo: (title: string, message: string, action?: ToastMessage['action']) => void;
  clearToasts: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
  maxToasts?: number;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ 
  children, 
  maxToasts = 5 
}) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const generateId = useCallback(() => {
    return `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  const showToast = useCallback((toast: Omit<ToastMessage, 'id'>) => {
    const newToast: ToastMessage = {
      ...toast,
      id: generateId(),
    };

    setToasts(prev => {
      const updated = [newToast, ...prev];
      // Limit the number of toasts
      return updated.slice(0, maxToasts);
    });
  }, [generateId, maxToasts]);

  const showError = useCallback((title: string, message: string, action?: ToastMessage['action']) => {
    showToast({
      type: 'error',
      title,
      message,
      action,
      duration: 7000, // Longer duration for errors
    });
  }, [showToast]);

  const showWarning = useCallback((title: string, message: string, action?: ToastMessage['action']) => {
    showToast({
      type: 'warning',
      title,
      message,
      action,
      duration: 6000,
    });
  }, [showToast]);

  const showSuccess = useCallback((title: string, message: string, action?: ToastMessage['action']) => {
    showToast({
      type: 'success',
      title,
      message,
      action,
      duration: 4000,
    });
  }, [showToast]);

  const showInfo = useCallback((title: string, message: string, action?: ToastMessage['action']) => {
    showToast({
      type: 'info',
      title,
      message,
      action,
      duration: 5000,
    });
  }, [showToast]);

  const clearToasts = useCallback(() => {
    setToasts([]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const value: ToastContextType = {
    showToast,
    showError,
    showWarning,
    showSuccess,
    showInfo,
    clearToasts,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-container">
        {toasts.map(toast => (
          <ErrorToast
            key={toast.id}
            message={toast}
            onClose={removeToast}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export default ToastContext;